import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Clock,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Trophy,
  BookOpen,
  GraduationCap,
  RotateCcw,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import {
  useAssessment,
  useStartAssessmentAttempt,
  useSubmitAssessmentAttempt,
  useMyAssessmentAttempts,
} from '@/api/hooks';
import type { AssessmentQuestion } from '@/api/client';
import { useTrackEntityView, useTrackEntityAction } from '@/hooks/use-entity-tracker';

export const Route = createFileRoute('/learn/assessments/$assessmentId')({
  component: AssessmentPage,
});

type AssessmentState = 'intro' | 'in_progress';

function AssessmentPage() {
  const { assessmentId } = Route.useParams();
  const navigate = useNavigate();

  const { data: assessment, isLoading, error } = useAssessment(assessmentId);
  const { data: previousAttempts = [] } = useMyAssessmentAttempts(assessmentId);
  const startAttemptMutation = useStartAssessmentAttempt();
  const submitAttemptMutation = useSubmitAssessmentAttempt();
  const trackAction = useTrackEntityAction();

  const [state, setState] = useState<AssessmentState>('intro');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  // The "Completed" card is a modal overlay over the in-progress player. It
  // owns the submit lifecycle in-place: idle (Cancel / Submit) → loading →
  // result (score + OK). See reqs 3–5.
  const [showCompletedCard, setShowCompletedCard] = useState(false);
  const [submittedResult, setSubmittedResult] = useState<{
    score: number;
    maxScore: number;
    percentage: number;
    passed: boolean;
  } | null>(null);

  const questions = assessment?.questions || [];
  const currentQuestion = questions[currentQuestionIndex];
  const totalQuestions = questions.length;

  // Track assessment view
  useTrackEntityView({
    entityType: 'assessment',
    entityId: assessmentId,
    entityName: assessment?.name || 'Untitled Assessment',
    metadata: {
      totalQuestions,
      passingScore: assessment?.passing_score,
      timeLimit: assessment?.time_limit,
      maxAttempts: assessment?.max_attempts,
      attemptCount: previousAttempts.length,
    },
  });

  // Timer effect
  useEffect(() => {
    if (state !== 'in_progress' || !assessment?.time_limit || timeRemaining === null) return;

    if (timeRemaining <= 0) {
      handleSubmit();
      return;
    }

    const timer = setInterval(() => {
      setTimeRemaining((prev) => (prev !== null ? prev - 1 : null));
    }, 1000);

    return () => clearInterval(timer);
  }, [state, timeRemaining, assessment?.time_limit]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStart = () => {
    startAttemptMutation.mutate(assessmentId, {
      onSuccess: (attempt) => {
        setAttemptId(attempt.id);
        setState('in_progress');
        if (assessment?.time_limit) {
          setTimeRemaining(assessment.time_limit * 60); // Convert minutes to seconds
        }
      },
    });
  };

  const handleAnswer = (questionId: string, answer: unknown) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: answer,
    }));

    const isLast = currentQuestionIndex === totalQuestions - 1;

    // On the last question, selecting an answer raises the Completed card
    // instead of auto-advancing (reqs 2 & 3 — there is no inline Submit).
    if (isLast) {
      setShowCompletedCard(true);
      return;
    }

    // Auto-advance for single-answer types where the selection is final on
    // one tap. Skip multi-select / text input (user is still composing).
    // Small delay so the selected state animates in before the transition.
    const isSingleAnswer =
      currentQuestion?.type === 'multiple_choice' ||
      currentQuestion?.type === 'true_false';
    if (isSingleAnswer) {
      setTimeout(() => {
        setCurrentQuestionIndex((prev) => prev + 1);
      }, 300);
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  const handleSubmit = () => {
    if (!attemptId) return;

    // Keep the card up and let it show the in-place loader. Also covers the
    // timer-expiry path, which calls handleSubmit directly.
    setShowCompletedCard(true);
    submitAttemptMutation.mutate(
      { attemptId, answers },
      {
        onSuccess: (result) => {
          setSubmittedResult({
            score: result.score ?? 0,
            maxScore: result.max_score ?? 0,
            percentage: result.percentage ?? 0,
            passed: result.passed ?? false,
          });

          // Track assessment completion
          trackAction('completed', 'assessment', assessmentId, assessment?.name || 'Untitled Assessment', {
            score: result.score,
            maxScore: result.max_score,
            percentage: result.percentage,
            passed: result.passed,
            attemptNumber: (previousAttempts.length || 0) + 1,
          });
        },
      }
    );
  };

  // OK on the result card returns to the course screen (req 5).
  const handleOk = () => {
    if (assessment?.course_id) {
      navigate({ to: '/learn/courses/$courseId', params: { courseId: assessment.course_id } });
    } else {
      navigate({ to: '/learn' });
    }
  };

  const handleCancelCard = () => setShowCompletedCard(false);

  // Try Again from the result card: reset the player and start a fresh attempt
  // (only offered while attempts remain). Keeps the tone forward-looking.
  const handleTryAgain = () => {
    setSubmittedResult(null);
    setAnswers({});
    setCurrentQuestionIndex(0);
    setShowCompletedCard(false);
    startAttemptMutation.mutate(assessmentId, {
      onSuccess: (attempt) => {
        setAttemptId(attempt.id);
        if (assessment?.time_limit) {
          setTimeRemaining(assessment.time_limit * 60);
        }
      },
    });
  };

  const answeredCount = Object.keys(answers).length;
  const canSubmit = answeredCount === totalQuestions;

  // Check if user has attempts remaining
  const maxAttempts = assessment?.max_attempts ?? null;
  const attemptCount = previousAttempts.length;
  const hasAttemptsRemaining = maxAttempts === null || attemptCount < maxAttempts;
  const bestAttempt = previousAttempts.reduce(
    (best, curr) => (curr.percentage ?? 0) > (best?.percentage ?? 0) ? curr : best,
    previousAttempts[0]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !assessment) {
    return (
      <div className="w-full min-w-0 max-w-2xl mx-auto">
        <Link
          to="/learn"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Learning
        </Link>
        <div className="bg-card rounded-xl border border-border p-8 text-center">
          <h2 className="text-xl font-serif italic text-foreground mb-2">Assessment Not Found</h2>
          <p className="text-muted-foreground">
            The assessment you're looking for doesn't exist or has been removed.
          </p>
        </div>
      </div>
    );
  }

  // Intro screen
  if (state === 'intro') {
    return (
      <div className="w-full min-w-0 max-w-2xl mx-auto">
        <Link
          to="/learn"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Learning
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card rounded-xl border border-border p-8"
        >
          {/* Assessment header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto bg-primary/15 rounded-xl flex items-center justify-center mb-4">
              <BookOpen className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-3xl font-serif italic text-foreground mb-2">{assessment.name}</h1>
            <p className="text-muted-foreground">{assessment.description}</p>
          </div>

          {/* Assessment info */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="bg-secondary/50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-foreground">{totalQuestions}</p>
              <p className="text-sm text-muted-foreground">Questions</p>
            </div>
            <div className="bg-secondary/50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-foreground">{assessment.passing_score}%</p>
              <p className="text-sm text-muted-foreground">Passing Score</p>
            </div>
            {assessment.time_limit && (
              <div className="bg-secondary/50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-foreground">{assessment.time_limit}</p>
                <p className="text-sm text-muted-foreground">Minutes</p>
              </div>
            )}
            {maxAttempts && (
              <div className="bg-secondary/50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-foreground">
                  {attemptCount}/{maxAttempts}
                </p>
                <p className="text-sm text-muted-foreground">Attempts Used</p>
              </div>
            )}
          </div>

          {/* Instructions */}
          {assessment.instructions && (
            <div className="bg-secondary/30 rounded-lg p-4 mb-8">
              <h3 className="font-semibold text-foreground mb-2">Instructions</h3>
              <p className="text-sm text-muted-foreground">{assessment.instructions}</p>
            </div>
          )}

          {/* Previous attempts */}
          {previousAttempts.length > 0 && (
            <div className="mb-8">
              <h3 className="font-semibold text-foreground mb-3">Previous Attempts</h3>
              <div className="space-y-2">
                {previousAttempts.slice(0, 3).map((attempt) => (
                  <div
                    key={attempt.id}
                    className="flex items-center justify-between bg-secondary/30 rounded-lg px-4 py-2"
                  >
                    <span className="text-sm text-muted-foreground">
                      Attempt {attempt.attempt_number}
                    </span>
                    <div className="flex items-center gap-2">
                      <span
                        className={`font-semibold ${
                          attempt.passed ? 'text-success' : 'text-muted-foreground'
                        }`}
                      >
                        {attempt.percentage?.toFixed(0)}%
                      </span>
                      {attempt.passed && (
                        <CheckCircle2 className="w-4 h-4 text-success" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Start button */}
          {hasAttemptsRemaining ? (
            <button
              onClick={handleStart}
              disabled={startAttemptMutation.isPending}
              className="w-full px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {startAttemptMutation.isPending ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Starting...
                </>
              ) : (
                <>
                  Start Assessment
                  <ChevronRight className="w-5 h-5" />
                </>
              )}
            </button>
          ) : (
            <div className="text-center">
              <p className="text-muted-foreground mb-4">You've used all available attempts.</p>
              {bestAttempt && bestAttempt.passed && (
                <p className="text-success">You passed with {bestAttempt.percentage?.toFixed(0)}%!</p>
              )}
            </div>
          )}
        </motion.div>
      </div>
    );
  }

  // Quiz in progress
  if (state === 'in_progress' && currentQuestion) {
    const isLastQuestion = currentQuestionIndex === totalQuestions - 1;

    return (
      <div className="w-full min-w-0 max-w-3xl mx-auto">
        {/* Header with timer and progress */}
        <div className="flex items-center justify-between mb-6">
          <div className="text-sm text-muted-foreground">
            Question {currentQuestionIndex + 1} of {totalQuestions}
          </div>
          {timeRemaining !== null && (
            <div
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                timeRemaining < 60 ? 'bg-danger/15 text-danger' : 'bg-secondary'
              }`}
            >
              <Clock className="w-4 h-4" />
              <span className="font-mono font-semibold">{formatTime(timeRemaining)}</span>
            </div>
          )}
        </div>

        {/* Progress bar */}
        <div className="w-full h-2 bg-secondary rounded-full mb-6 overflow-hidden">
          <motion.div
            className="h-full bg-primary"
            initial={{ width: 0 }}
            animate={{ width: `${((currentQuestionIndex + 1) / totalQuestions) * 100}%` }}
          />
        </div>

        {/* Question card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion.id}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="bg-card rounded-xl border border-border p-6 mb-6"
          >
            <QuestionRenderer
              question={currentQuestion}
              answer={answers[currentQuestion.id]}
              onAnswer={(answer) => handleAnswer(currentQuestion.id, answer)}
            />
          </motion.div>
        </AnimatePresence>

        {/* Navigation — no inline Submit (req 2); the last question raises the
            Completed card instead. */}
        <div className="flex items-center justify-between">
          <button
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
            className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            Previous
          </button>

          <div className="flex items-center gap-2 min-w-0 flex-1 justify-center overflow-x-auto scrollbar-hide px-2">
            {/* Question dots — shrink-and-scroll so a long quiz (many dots)
                never forces the page wider than the viewport on mobile. */}
            {questions.map((q, idx) => (
              <button
                key={q.id}
                onClick={() => setCurrentQuestionIndex(idx)}
                className={`w-3 h-3 rounded-full flex-shrink-0 transition-all ${
                  idx === currentQuestionIndex
                    ? 'bg-primary scale-125'
                    : answers[q.id] !== undefined
                    ? 'bg-success'
                    : 'bg-secondary'
                }`}
              />
            ))}
          </div>

          {isLastQuestion ? (
            // Spacer keeps the dots centred now that Submit is gone.
            <div className="w-[92px]" aria-hidden />
          ) : (
            <button
              onClick={handleNext}
              className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-secondary transition-colors"
            >
              Next
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Answered count */}
        <div className="mt-6 text-center text-sm text-muted-foreground">
          {answeredCount} of {totalQuestions} questions answered
        </div>

        {/* Completed card — modal overlay (reqs 3–5) */}
        <AnimatePresence>
          {showCompletedCard && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.92 }}
                transition={{ type: 'spring', duration: 0.35 }}
                className="bg-card rounded-2xl border border-border shadow-lg p-8 text-center max-w-sm w-full"
              >
                {submittedResult ? (
                  /* Result sub-state: score + actions. Kept deliberately
                     low-key when not yet passing — no red, no harsh icon, no
                     pass-threshold call-out; just the score and a way forward. */
                  <>
                    <div
                      className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${
                        submittedResult.passed ? 'bg-success/15' : 'bg-primary/10'
                      }`}
                    >
                      {submittedResult.passed ? (
                        <Trophy className="w-8 h-8 text-success" />
                      ) : (
                        <RotateCcw className="w-8 h-8 text-primary" />
                      )}
                    </div>
                    <h2
                      className={`text-4xl font-serif italic mb-1 ${
                        submittedResult.passed ? 'text-success' : 'text-foreground'
                      }`}
                    >
                      {submittedResult.percentage.toFixed(0)}%
                    </h2>
                    <p className="text-sm text-muted-foreground mb-6">
                      {submittedResult.score} / {submittedResult.maxScore} points
                    </p>
                    <div className="space-y-3">
                      {!submittedResult.passed && hasAttemptsRemaining && (
                        <button
                          onClick={handleTryAgain}
                          disabled={startAttemptMutation.isPending}
                          className="w-full px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                          <RotateCcw className="w-5 h-5" />
                          Try Again
                        </button>
                      )}
                      <button
                        onClick={handleOk}
                        className={`w-full px-6 py-3 rounded-xl transition-colors ${
                          !submittedResult.passed && hasAttemptsRemaining
                            ? 'border border-border font-medium hover:bg-secondary'
                            : 'bg-primary hover:bg-primary/90 text-primary-foreground font-semibold'
                        }`}
                      >
                        OK
                      </button>
                    </div>
                  </>
                ) : submitAttemptMutation.isPending ? (
                  /* Loading sub-state */
                  <>
                    <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
                    <p className="text-muted-foreground">Submitting for scoring…</p>
                  </>
                ) : (
                  /* Idle sub-state: Completed + Cancel / Submit */
                  <>
                    <div className="w-16 h-16 mx-auto rounded-full bg-success/15 flex items-center justify-center mb-4">
                      <GraduationCap className="w-8 h-8 text-success" />
                    </div>
                    <h2 className="text-3xl font-serif italic text-foreground mb-2">Completed</h2>
                    <p className="text-sm text-muted-foreground mb-6">
                      You've reached the end of the assessment. Submit your answers for scoring?
                    </p>
                    {!canSubmit && (
                      <p className="text-xs text-warning mb-4">
                        {totalQuestions - answeredCount} question
                        {totalQuestions - answeredCount !== 1 ? 's' : ''} still unanswered.
                      </p>
                    )}
                    <div className="flex gap-3">
                      <button
                        onClick={handleCancelCard}
                        className="flex-1 px-6 py-3 border border-border rounded-xl font-medium hover:bg-secondary transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        className="flex-1 px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Submit
                      </button>
                    </div>
                  </>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return null;
}

// Question Renderer Component
function QuestionRenderer({
  question,
  answer,
  onAnswer,
}: {
  question: AssessmentQuestion;
  answer: unknown;
  onAnswer: (answer: unknown) => void;
}) {
  const { type, question: questionText, options, image_url } = question;

  return (
    <div>
      {/* Question text */}
      <h3 className="text-lg font-semibold text-foreground mb-4">{questionText}</h3>

      {/* Question image */}
      {image_url && (
        <img
          src={image_url}
          alt="Question"
          className="max-w-full h-auto rounded-lg mb-4"
        />
      )}

      {/* Answer options based on type.
          Imported data shape: { label: 'A'|'B'|..., text: 'the answer text' }.
          `correct_answer` in the DB stores the LABEL, so we use label as the
          answer key and render `text` as the visible stem. */}
      {type === 'multiple_choice' && (
        <div className="space-y-3">
          {(options as { label: string; text: string }[])?.map((option, idx) => {
            const selected = answer === option.label;
            return (
              <button
                key={option.label ?? idx}
                onClick={() => onAnswer(option.label)}
                className={`w-full p-4 rounded-lg border text-left transition-all ${
                  selected
                    ? 'border-primary bg-primary/15'
                    : 'border-border hover:border-input hover:bg-secondary/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      selected
                        ? 'border-primary bg-primary'
                        : 'border-muted-foreground'
                    }`}
                  >
                    {selected && <div className="w-2 h-2 rounded-full bg-primary-foreground" />}
                  </div>
                  <span className="text-foreground">
                    <span className="font-semibold mr-2">{option.label}.</span>
                    {option.text}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {type === 'multiple_select' && (
        <div className="space-y-3">
          {(options as { label: string; text: string }[])?.map((option, idx) => {
            const selected = Array.isArray(answer) && answer.includes(option.label);
            return (
              <button
                key={option.label ?? idx}
                onClick={() => {
                  const current = Array.isArray(answer) ? answer : [];
                  if (selected) {
                    onAnswer(current.filter((v) => v !== option.label));
                  } else {
                    onAnswer([...current, option.label]);
                  }
                }}
                className={`w-full p-4 rounded-lg border text-left transition-all ${
                  selected
                    ? 'border-primary bg-primary/15'
                    : 'border-border hover:border-input hover:bg-secondary/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      selected ? 'border-primary bg-primary' : 'border-muted-foreground'
                    }`}
                  >
                    {selected && <CheckCircle2 className="w-4 h-4 text-primary-foreground" />}
                  </div>
                  <span className="text-foreground">
                    <span className="font-semibold mr-2">{option.label}.</span>
                    {option.text}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {type === 'true_false' && (
        <div className="grid grid-cols-2 gap-4">
          {['true', 'false'].map((value) => (
            <button
              key={value}
              onClick={() => onAnswer(value === 'true')}
              className={`p-6 rounded-lg border text-center font-semibold transition-all ${
                answer === (value === 'true')
                  ? 'border-primary bg-primary/15 text-primary'
                  : 'border-border hover:border-input hover:bg-secondary/50'
              }`}
            >
              {value === 'true' ? 'True' : 'False'}
            </button>
          ))}
        </div>
      )}

      {type === 'short_answer' && (
        <input
          type="text"
          value={(answer as string) || ''}
          onChange={(e) => onAnswer(e.target.value)}
          placeholder="Type your answer..."
          className="w-full p-4 rounded-lg border border-border bg-secondary/30 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
        />
      )}

      {type === 'essay' && (
        <textarea
          value={(answer as string) || ''}
          onChange={(e) => onAnswer(e.target.value)}
          placeholder="Write your answer..."
          rows={6}
          className="w-full p-4 rounded-lg border border-border bg-secondary/30 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all resize-none"
        />
      )}

      {/* Points indicator */}
      <div className="mt-4 text-right text-sm text-muted-foreground">
        {question.points} point{question.points !== 1 ? 's' : ''}
      </div>
    </div>
  );
}
